import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useEffect, useMemo, useState } from 'react';
import { combinarFechaConHoraActual } from '../../../utils/horaLocal';

const filaVacia = () => ({
  materialDestinoId: '',
  cantidadKg: '',
  precioCompraKg: '',
});

export function ModalLimpieza({
  open,
  onClose,
  mode = 'create',
  gastoOrigen = null,
  productos = [],
  fechaSeleccionada = null,
  díaBloqueado = false,
  calcularStock,
  onCrear,
  onConvertir,
}) {
  const [step, setStep] = useState('form');
  const [materialOrigenId, setMaterialOrigenId] = useState('');
  const [cantidadDescontadaKg, setCantidadDescontadaKg] = useState('');
  const [trabajadorDescripcion, setTrabajadorDescripcion] = useState('');
  const [observacion, setObservacion] = useState('');
  const [metodoPago, setMetodoPago] = useState('EFECTIVO');
  const [motivo, setMotivo] = useState('');
  const [resultados, setResultados] = useState([filaVacia()]);
  const [guardando, setGuardando] = useState(false);

  const fechaEfectiva = mode === 'conversion' && gastoOrigen?.fecha
    ? gastoOrigen.fecha
    : fechaSeleccionada;

  useEffect(() => {
    if (!open) return;
    setStep('form');
    setMaterialOrigenId('');
    setCantidadDescontadaKg('');
    setTrabajadorDescripcion('');
    setObservacion('');
    setMotivo('');
    setResultados([filaVacia()]);
    if (mode === 'conversion' && gastoOrigen) {
      setMetodoPago(gastoOrigen.metodoPago || 'EFECTIVO');
      if (gastoOrigen.descripcion) setTrabajadorDescripcion(gastoOrigen.descripcion);
    } else {
      setMetodoPago('EFECTIVO');
    }
  }, [open, mode, gastoOrigen]);

  const total = useMemo(() =>
    resultados.reduce((a, r) => {
      const c = parseFloat(r.cantidadKg);
      const p = parseFloat(r.precioCompraKg);
      if (isNaN(c) || isNaN(p)) return a;
      return a + c * p;
    }, 0),
  [resultados]);

  const montoGasto = gastoOrigen?.monto ?? null;
  const montoMismatch = mode === 'conversion' && montoGasto != null && Math.abs(total - montoGasto) > 0.01;

  const stockOrigen = materialOrigenId && calcularStock
    ? calcularStock(materialOrigenId)
    : null;

  const stockTrasOp = stockOrigen != null && cantidadDescontadaKg
    ? stockOrigen - parseFloat(cantidadDescontadaKg)
    : null;

  const actualizarResultado = (index, field, value) => {
    setResultados(prev => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const agregarFila = () => setResultados(prev => [...prev, filaVacia()]);

  const eliminarFila = (index) => {
    setResultados(prev => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const validarFormulario = () => {
    if (mode === 'conversion' && !motivo.trim()) {
      alert('Indique el motivo de la conversión');
      return false;
    }
    if (!materialOrigenId || !cantidadDescontadaKg) {
      alert('Complete material origen y kg a descontar');
      return false;
    }
    const cant = parseFloat(cantidadDescontadaKg);
    if (isNaN(cant) || cant <= 0) {
      alert('Kg a descontar no válidos');
      return false;
    }
    if (mode === 'create' && díaBloqueado) {
      alert('El día está bloqueado por arqueo');
      return false;
    }
    for (const r of resultados) {
      if (!r.materialDestinoId || !r.cantidadKg || r.precioCompraKg === '') {
        alert('Complete todas las líneas de material recuperado');
        return false;
      }
      if (String(r.materialDestinoId) === String(materialOrigenId)) {
        alert('Origen y destino no pueden ser el mismo material');
        return false;
      }
    }
    const sumRec = resultados.reduce((a, r) => a + parseFloat(r.cantidadKg), 0);
    if (sumRec > cant) {
      alert('Los kg recuperados no pueden superar los descontados del origen');
      return false;
    }
    if (montoMismatch) {
      const ok = window.confirm(
        `El total de la limpieza (Bs ${total.toFixed(2)}) no coincide con el gasto (Bs ${montoGasto.toFixed(2)}). ¿Continuar?`
      );
      if (!ok) return false;
    }
    return true;
  };

  const buildPayload = () => ({
    fecha: combinarFechaConHoraActual(
      fechaEfectiva,
      mode === 'conversion' && gastoOrigen?.fecha ? new Date(gastoOrigen.fecha) : new Date()
    ),
    materialOrigenId,
    cantidadDescontadaKg: parseFloat(cantidadDescontadaKg),
    resultados: resultados.map(r => ({
      materialDestinoId: r.materialDestinoId,
      cantidadKg: parseFloat(r.cantidadKg),
      precioCompraKg: parseFloat(r.precioCompraKg),
    })),
    trabajadorDescripcion,
    observacion,
    metodoPago,
    motivo: mode === 'conversion' ? motivo.trim() : undefined,
  });

  const irAPreview = () => {
    if (!validarFormulario()) return;
    setStep('preview');
  };

  const confirmar = async () => {
    if (!validarFormulario()) return;
    setGuardando(true);
    try {
      const payload = buildPayload();
      if (mode === 'conversion') {
        await onConvertir?.(gastoOrigen.id, payload);
      } else {
        await onCrear?.(payload);
      }
      onClose();
    } catch (e) {
      const msg = e.response?.data?.message || e.message || 'Error al guardar';
      alert(msg);
    } finally {
      setGuardando(false);
    }
  };

  const titulo = mode === 'conversion' ? 'Convertir gasto en limpieza' : 'Material de limpieza';

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{titulo}</DialogTitle>
      <DialogContent>
        {mode === 'conversion' && gastoOrigen && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Se anulará el gasto de Bs {Number(gastoOrigen.monto).toFixed(2)} y se creará una limpieza
            vinculada con la misma fecha efectiva. El pago pasará a registrarse como compra del material recuperado.
          </Alert>
        )}

        {step === 'preview' ? (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Vista previa</Typography>
            <Typography variant="body2">
              Origen: {productos.find(p => p.id === materialOrigenId)?.nombre} — {cantidadDescontadaKg} kg
            </Typography>
            {stockTrasOp != null && (
              <Typography variant="body2" color={stockTrasOp < 0 ? 'error' : 'text.secondary'}>
                Stock origen tras operación (hoy): {stockTrasOp.toFixed(2)} kg
                {stockTrasOp < 0 && ' — el servidor validará también en la fecha efectiva'}
              </Typography>
            )}
            {resultados.map((r, i) => (
              <Typography key={i} variant="body2">
                + {productos.find(p => p.id === r.materialDestinoId)?.nombre}: {r.cantidadKg} kg × Bs {r.precioCompraKg}
              </Typography>
            ))}
            <Typography fontWeight={600}>Total pago: Bs {total.toFixed(2)} ({metodoPago})</Typography>
            {montoMismatch && (
              <Alert severity="warning">Total distinto al monto del gasto original.</Alert>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {mode === 'conversion' && (
              <TextField
                label="Motivo de conversión"
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                required
                fullWidth
                multiline
                minRows={2}
              />
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Select
                fullWidth
                displayEmpty
                value={materialOrigenId}
                onChange={e => setMaterialOrigenId(e.target.value)}
              >
                <MenuItem value="" disabled>Material origen</MenuItem>
                {productos.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                ))}
              </Select>
              <TextField
                label="Kg a descontar"
                type="number"
                value={cantidadDescontadaKg}
                onChange={e => setCantidadDescontadaKg(e.target.value)}
                fullWidth
              />
            </Stack>

            {stockOrigen != null && materialOrigenId && (
              <Typography variant="caption" color="text.secondary">
                Stock actual (origen): {stockOrigen} kg
              </Typography>
            )}

            <Divider>Materiales recuperados (compra)</Divider>

            {resultados.map((row, index) => (
              <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                <Select
                  fullWidth
                  displayEmpty
                  value={row.materialDestinoId}
                  onChange={e => actualizarResultado(index, 'materialDestinoId', e.target.value)}
                >
                  <MenuItem value="" disabled>Material</MenuItem>
                  {productos.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                  ))}
                </Select>
                <TextField
                  label="Kg"
                  type="number"
                  value={row.cantidadKg}
                  onChange={e => actualizarResultado(index, 'cantidadKg', e.target.value)}
                  sx={{ minWidth: 90 }}
                />
                <TextField
                  label="Bs/kg compra"
                  type="number"
                  value={row.precioCompraKg}
                  onChange={e => actualizarResultado(index, 'precioCompraKg', e.target.value)}
                  sx={{ minWidth: 110 }}
                />
                <IconButton onClick={() => eliminarFila(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}

            <Button startIcon={<AddCircleIcon />} onClick={agregarFila}>
              Agregar material recuperado
            </Button>

            <TextField
              label="Trabajador / descripción"
              value={trabajadorDescripcion}
              onChange={e => setTrabajadorDescripcion(e.target.value)}
              fullWidth
            />
            <TextField
              label="Observación"
              value={observacion}
              onChange={e => setObservacion(e.target.value)}
              fullWidth
            />

            <Box>
              <Typography variant="caption" sx={{ mr: 2 }}>Método de pago</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={metodoPago}
                onChange={(_, v) => v && setMetodoPago(v)}
              >
                <ToggleButton value="EFECTIVO">Efectivo</ToggleButton>
                <ToggleButton value="QR">QR</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Typography fontWeight={600}>
              Total a pagar: Bs {total.toFixed(2)}
              {mode === 'conversion' && montoGasto != null && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  (gasto: Bs {montoGasto.toFixed(2)})
                </Typography>
              )}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        {step === 'form' ? (
          <Button variant="contained" onClick={irAPreview}>Vista previa</Button>
        ) : (
          <>
            <Button onClick={() => setStep('form')}>Volver</Button>
            <Button variant="contained" onClick={confirmar} disabled={guardando}>
              {mode === 'conversion' ? 'Anular gasto y crear limpieza' : 'Registrar limpieza'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
